/* eslint-disable class-methods-use-this */
window.crazyeggExitSurvey = (() => {
  /**
   * @class CrazyEggExitSurvey
   * @description This class is for handling Crazy Egg exit surveys on a global scale.
   * @file global/js/modules/crazy-egg-exit-survey.js
   */

  /*
    Config object to be placed on the page
    --------------------------------------
    let CE_CONFIG = {
      surveyId: "",
    };
    --------------------------------------
    Surveys and other config values can be
    modified programmatically via
    the class methods.
  */

  class CrazyEggExitSurvey {
    constructor() {
      const classInstance = this;
      this.config = {};
      this.#setCrazyEggAPI();
      this.#setConfig(classInstance);
    }

    // Default params as specified by Crazy Egg
    // Docs: https://support.crazyegg.com/hc/en-us/articles/9923784159891-What-triggers-the-behavior-when-a-visitor-is-about-to-leave-the-page
    get defaultExitParams() {
      return {
        onMouseLeavesWindow: true,
        onFastSwipe: true,
        onLinkHover: true,
      };
    }

    addSurvey(id) {
      this.config.surveyId = id;
    }

    removeSurvey() {
      if (this.config.surveyId.length > 0) {
        this.config.surveyId = '';
      }
    }

    getEnabledSurvey() {
      return this.config.surveyId;
    }

    #setCrazyEggAPI() {
      if (!window.CE_API) window.CE_API = [];
    }

    #setConfig(classInstance) {
      // If config is undefined, define it
      if (!window.CE_CONFIG) {
        const script = document.createElement('script');
        script.innerHTML = `
          let CE_CONFIG = {
            surveyId: '',
          };
        `;
        document.body.prepend(script);
      }
      // eslint-disable-next-line no-undef
      const configOnPage = { ...CE_CONFIG };

      // Docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
      this.config = new Proxy(configOnPage, {
        get(target, key) {
          return target[key];
        },
        set(target, key, value) {
          const isOldStringEmpty = target[key] === '';
          const isNewStringEmpty = value === '';
          const isNewStringPopulated = value.length > 0;

          // NOTE: will be adding more config properties in the future
          switch (key) {
            case 'surveyId':
              if (isOldStringEmpty && isNewStringPopulated) {
                classInstance.#setEventListeners();
              }
              if (isNewStringEmpty) {
                classInstance.#removeEventListeners();
              }
              break;

            default:
              break;
          }

          target[key] = value;
          return true;
        },
      });

      // Set event listeners if surveyId is populated on load
      if (this.config.surveyId) {
        this.#setEventListeners();
      }
    }

    #triggerSurvey() {
      const { surveyId } = this.config;

      if (!surveyId) {
        throw new Error('no surveys currently enabled');
      } else {
        this.#removeEventListeners();
        window.CE_API.push(() => {
          // eslint-disable-next-line no-undef
          CE2.showSurvey(surveyId);
        });
      }
    }

    #setEventListeners() {
      const { onMouseLeavesWindow, onFastSwipe, onLinkHover } = this.defaultExitParams;
      this.listenerController = new AbortController();

      if (onMouseLeavesWindow) {
        this.#addMouseLeaveListener();
      }
      if (onFastSwipe) {
        this.#addMobileSwipeListener();
      }
      if (onLinkHover) {
        this.#addLinkHoverListener();
      }
    }

    #removeEventListeners() {
      if (this.listenerController) this.listenerController.abort();
      this.listenerController = null;
    }

    #addMouseLeaveListener() {
      // Triggers when leaving window from the side
      document.addEventListener(
        'mouseleave',
        () => {
          this.#triggerSurvey();
        },
        { signal: this.listenerController.signal },
      );

      // Triggers just before leaving top of window
      document.addEventListener(
        'mousemove',
        (event) => {
          if (event.pageY <= 100) {
            this.#triggerSurvey();
          }
        },
        { signal: this.listenerController.signal },
      );
    }

    #addLinkHoverListener() {
      // eslint-disable-next-line no-restricted-globals
      const { origin } = location;
      const anchorsOnPage = document.querySelectorAll('a');
      const externalAnchors = Array.from(anchorsOnPage).filter((item) => item.origin !== origin);

      externalAnchors.forEach((element) => {
        let isHovered = false;

        element.addEventListener(
          'mouseover',
          () => {
            isHovered = true;

            setTimeout(() => {
              if (isHovered) {
                this.#triggerSurvey();
              }
            }, 100);
          },
          { signal: this.listenerController.signal },
        );

        element.addEventListener(
          'mouseout',
          () => {
            isHovered = false;
          },
          { signal: this.listenerController.signal },
        );
      });
    }

    #addMobileSwipeListener() {
      let touchStartY = 0;
      let touchEndY = 0;

      document.addEventListener(
        'touchstart',
        () => {
          touchStartY = window.scrollY;
        },
        { signal: this.listenerController.signal },
      );

      document.addEventListener(
        'touchend',
        () => {
          touchEndY = window.scrollY;
          this.#handleSwipe(touchStartY, touchEndY);
        },
        { signal: this.listenerController.signal },
      );
    }

    #handleSwipe(touchStartY, touchEndY) {
      const distance = 100;
      const touchStartPlusDistance = touchStartY + distance;
      const touchEndPlusDistance = touchEndY + distance;
      const isSwipeDown = touchEndY < touchStartY && touchEndPlusDistance < touchStartY;
      const isSwipeUp = touchEndY > touchStartY && touchStartPlusDistance < touchEndY;

      if (isSwipeDown) {
        this.#triggerSurvey();
      }

      if (isSwipeUp) {
        this.#triggerSurvey();
      }
    }
  }
  return new CrazyEggExitSurvey();
})();
